import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck,
  Scale,
  Building2,
  Landmark,
  Users,
  Briefcase,
  Plane,
  AlertTriangle,
  Mail,
  Rocket,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import HussarTopNav from '../components/HussarTopNav';
import ProtocolBadge from '../components/ProtocolBadge';
import useRouteMeta from '../hooks/useRouteMeta';

/**
 * Public marketing surface for the Immigration & Bureaucracy Intelligence
 * pillar (Change Intelligence vertical under the HussarAi Protocol).
 *
 * STRICTLY a marketing/education page. NO public analyzer, NO upload form,
 * NO API calls, NO legal advice claims. The operational tool is admin-only.
 *
 * Brand boundary: positions immigration as a flagship vertical USE CASE
 * under HussarAi, NOT as the homepage identity. Frame is
 * "Immigration & Bureaucracy Intelligence" — never "AI immigration lawyer".
 *
 * Mounted at:
 *   /imigracja-i-urzedy        (canonical PL)
 *   /immigration-intelligence  (EN alias)
 */

const AUDIENCES = [
  { key: 'lawFirms',       Icon: Scale },
  { key: 'hr',             Icon: Briefcase },
  { key: 'relocation',     Icon: Plane },
  { key: 'administration', Icon: Landmark },
  { key: 'ngos',           Icon: Users },
  { key: 'compliance',     Icon: Building2 },
];

const ImmigrationIntelligencePublic = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'pl').startsWith('en') ? 'en' : 'pl';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  // Canonical = PL slug so search engines coalesce the alias under it.
  const pageUrl = `${origin}/imigracja-i-urzedy`;

  useRouteMeta({
    marker: 'imigracja-i-urzedy',
    title: t('immigrationIntelPublic.metaTitle'),
    description: t('immigrationIntelPublic.metaDescription'),
    canonical: pageUrl,
    locale: lang === 'en' ? 'en_GB' : 'pl_PL',
    ogUrl: pageUrl,
  });

  const monitors = t('immigrationIntelPublic.monitors.items', { returnObjects: true }) || [];
  const notDoing = t('immigrationIntelPublic.notDoing.items', { returnObjects: true }) || [];

  return (
    <div className="hussar-landing" data-testid="immigration-intel-public-page">
      <HussarTopNav />

      {/* Hero */}
      <section
        className="hussar-section hussar-hero"
        data-testid="immipub-hero"
        style={{ paddingTop: 80 }}
      >
        <div className="hussar-container">
          <div
            data-testid="immipub-hero-eyebrow"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 12px', borderRadius: 999,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(125, 211, 252, 0.06)',
              color: '#7DD3FC',
              fontSize: 11, letterSpacing: 1.2, fontWeight: 600,
              marginBottom: 18,
            }}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            {t('immigrationIntelPublic.heroEyebrow')}
          </div>
          <h1
            className="hussar-hero-title"
            data-testid="immipub-hero-h1"
            style={{ maxWidth: 920 }}
          >
            {t('immigrationIntelPublic.heroH1')}
          </h1>
          <p
            className="hussar-hero-subtitle"
            data-testid="immipub-hero-subtitle"
            style={{ maxWidth: 820, marginTop: 16 }}
          >
            {t('immigrationIntelPublic.heroSubtitle')}
          </p>
          <p
            data-testid="immipub-hero-safety-line"
            style={{
              marginTop: 14,
              padding: '10px 14px',
              borderRadius: 6,
              background: 'rgba(245,158,11,0.06)',
              borderLeft: '3px solid #F59E0B',
              fontSize: 13,
              fontWeight: 600,
              maxWidth: 820,
            }}
          >
            {t('immigrationIntelPublic.safetyLine')}
          </p>
          <ProtocolBadge testidPrefix="immipub-hero" />
        </div>
      </section>

      {/* Doctrine card */}
      <section
        className="hussar-section"
        data-testid="immipub-doctrine-section"
        style={{ paddingTop: 16 }}
      >
        <div className="hussar-container">
          <Card
            data-testid="immipub-doctrine-card"
            style={{
              padding: 24,
              background: 'rgba(125, 211, 252, 0.04)',
              borderLeft: '3px solid #7DD3FC',
            }}
          >
            <h2
              data-testid="immipub-doctrine-title"
              style={{ fontSize: 20, fontWeight: 700, margin: 0 }}
            >
              {t('immigrationIntelPublic.doctrine.title')}
            </h2>
            <p
              data-testid="immipub-doctrine-body"
              style={{ marginTop: 10, fontSize: 15, lineHeight: 1.55, opacity: 0.9 }}
            >
              {t('immigrationIntelPublic.doctrine.body')}
            </p>
            <p
              data-testid="immipub-doctrine-line"
              style={{
                marginTop: 14,
                padding: 10,
                borderRadius: 6,
                background: 'rgba(245,158,11,0.06)',
                borderLeft: '3px solid #F59E0B',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {t('immigrationIntelPublic.doctrine.line')}
            </p>
          </Card>
        </div>
      </section>

      {/* Audiences */}
      <section
        className="hussar-section"
        data-testid="immipub-audiences-section"
        style={{ paddingTop: 24 }}
      >
        <div className="hussar-container">
          <h2 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}
              data-testid="immipub-audiences-heading">
            {t('immigrationIntelPublic.audiences.heading')}
          </h2>
          <div
            data-testid="immipub-audiences-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 16, marginTop: 20,
            }}
          >
            {AUDIENCES.map(({ key, Icon }) => (
              <Card
                key={key}
                data-testid={`immipub-audience-${key}`}
                style={{
                  padding: 20,
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div
                  style={{
                    width: 36, height: 36, borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(125, 211, 252, 0.08)', color: '#7DD3FC',
                  }}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <h3 style={{ fontSize: 17, fontWeight: 600, margin: '12px 0 8px' }}>
                  {t(`immigrationIntelPublic.audiences.${key}.title`)}
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.55, opacity: 0.85, margin: 0 }}>
                  {t(`immigrationIntelPublic.audiences.${key}.body`)}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What it monitors + What it does NOT do — side by side */}
      <section
        className="hussar-section"
        data-testid="immipub-capabilities-section"
        style={{ paddingTop: 24 }}
      >
        <div className="hussar-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 16,
          }}>
            <Card
              data-testid="immipub-monitors-card"
              style={{ padding: 22, background: 'rgba(34, 197, 94, 0.04)', borderLeft: '3px solid #34D399' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle2 className="w-4 h-4" style={{ color: '#34D399' }} />
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}
                    data-testid="immipub-monitors-heading">
                  {t('immigrationIntelPublic.monitors.heading')}
                </h3>
              </div>
              <ul
                data-testid="immipub-monitors-list"
                style={{ marginTop: 12, paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}
              >
                {monitors.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Card>
            <Card
              data-testid="immipub-not-doing-card"
              style={{ padding: 22, background: 'rgba(239, 68, 68, 0.04)', borderLeft: '3px solid #F87171' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <XCircle className="w-4 h-4" style={{ color: '#F87171' }} />
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}
                    data-testid="immipub-not-doing-heading">
                  {t('immigrationIntelPublic.notDoing.heading')}
                </h3>
              </div>
              <ul
                data-testid="immipub-not-doing-list"
                style={{ marginTop: 12, paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}
              >
                {notDoing.map((item, idx) => (
                  <li key={item} data-testid={`immipub-not-doing-item-${idx}`}>{item}</li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="hussar-section"
        data-testid="immipub-cta-section"
        style={{ paddingTop: 30, paddingBottom: 80 }}
      >
        <div className="hussar-container">
          <Card
            style={{
              padding: 28,
              background: 'rgba(125, 211, 252, 0.05)',
              borderLeft: '3px solid #7DD3FC',
            }}
          >
            <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}
                data-testid="immipub-cta-headline">
              {t('immigrationIntelPublic.cta.headline')}
            </h2>
            <p style={{ marginTop: 10, fontSize: 15, opacity: 0.9, lineHeight: 1.55 }}
               data-testid="immipub-cta-body">
              {t('immigrationIntelPublic.cta.body')}
            </p>
            <div style={{ marginTop: 18, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link
                to={t('immigrationIntelPublic.cta.primaryTo')}
                className="hussar-btn-primary"
                data-testid="immipub-cta-primary"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 22px', borderRadius: 8,
                  textDecoration: 'none', fontWeight: 600,
                }}
              >
                {t('immigrationIntelPublic.cta.primary')} <Rocket className="w-4 h-4" />
              </Link>
              <a
                href="mailto:audyt@hussarai.org?subject=Monitoring%20zmian%20-%20imigracja%20i%20urzedy"
                className="hussar-btn-secondary"
                data-testid="immipub-cta-secondary"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 22px', borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.12)',
                  textDecoration: 'none', fontWeight: 600,
                  color: 'var(--hussar-text-primary, #E5E7EB)',
                }}
              >
                {t('immigrationIntelPublic.cta.secondary')} <Mail className="w-4 h-4" />
              </a>
            </div>
          </Card>

          <p
            data-testid="immipub-footer-doctrine"
            style={{
              marginTop: 22, fontSize: 12, opacity: 0.55,
              textAlign: 'left', fontStyle: 'italic',
            }}
          >
            <AlertTriangle className="w-3 h-3 inline mr-1" style={{ verticalAlign: '-2px' }} />
            {t('immigrationIntelPublic.doctrine.line')}
          </p>
          <p
            data-testid="immipub-footer-meta"
            style={{
              marginTop: 6, fontSize: 12, opacity: 0.45,
              textAlign: 'left',
            }}
          >
            {t('immigrationIntelPublic.footerMeta')}
          </p>
        </div>
      </section>
    </div>
  );
};

export default ImmigrationIntelligencePublic;
